import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Menu,
  Minus,
  Plus,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  X
} from "lucide-react";
import { products, type CatalogPayload, type Product, type ProductCollection } from "./data/products";
import { createCartPermalink, createShopifyCheckout, hasShopifyConfig } from "./lib/shopify";

type CartItem = {
  product: Product;
  image: string;
  size: string;
  color: string;
  quantity: number;
  variantId?: number;
  unitPrice: number;
};

type LandingPage = {
  slug: string;
  label: string;
  collection: string;
  collectionHandles: string[];
  tags: string[];
  kicker: string;
  headline: string;
  body: string;
  tone: string;
  artLabel: string;
  artNote: string;
};

const landingPages: LandingPage[] = [
  {
    slug: "witchy",
    label: "Witchy",
    collection: "Wizardry Shirts",
    collectionHandles: ["wizardry", "wizardry-shirts", "witchy"],
    tags: ["wizardry collection", "witch", "wizard", "spell", "magic", "potion", "spooky academy"],
    kicker: "For the magically unwell",
    headline: "A little witchy. A lot unhinged.",
    body: "Bookish spells, potion problems, and magical-school mischief.",
    tone: "wizardry",
    artLabel: "Mischief, but make it magical.",
    artNote: "The Witchy Collection"
  },
  {
    slug: "horny",
    label: "Horny",
    collection: "Horny Collection",
    collectionHandles: ["horny", "horny-collection"],
    tags: ["horny collection", "adult humor", "crude", "rude", "shock humor"],
    kicker: "Definitely not work appropriate",
    headline: "Bad taste has never looked better.",
    body: "Crude, blunt, and built to get a second look.",
    tone: "horny",
    artLabel: "Wear your inside thoughts.",
    artNote: "The Horny Collection"
  },
  {
    slug: "nurses",
    label: "Nurses",
    collection: "Nurse Shirts",
    collectionHandles: ["nurses", "nurse-shirts", "nursing"],
    tags: ["nurse", "nursing", "rn", "night shift"],
    kicker: "Shift-proof sarcasm",
    headline: "Nurse shirts for people who charted it, lived it, and still showed up.",
    body: "Night-shift humor only nurses fully understand.",
    tone: "clinical",
    artLabel: "Twelve hours. No lunch. Still funny.",
    artNote: "The Nurse Collection"
  },
  {
    slug: "work-shirts",
    label: "Work Shirts",
    collection: "Work Shirts",
    collectionHandles: ["work-shirts", "office-shirts", "office", "work"],
    tags: ["work", "office", "coworker", "meeting", "corporate"],
    kicker: "Corporate survival gear",
    headline: "Office shirts for meetings that absolutely should have stayed emails.",
    body: "Corporate survival gear for meetings that should've been emails.",
    tone: "office",
    artLabel: "This meeting could've been a shirt.",
    artNote: "The Work Collection"
  },
  {
    slug: "holiday",
    label: "Holiday",
    collection: "Holiday Shirts",
    collectionHandles: ["holiday", "holiday-shirts", "christmas", "halloween"],
    tags: ["holiday", "christmas", "halloween", "costume"],
    kicker: "Seasonal bad decisions",
    headline: "Holiday shirts for people who technically participated.",
    body: "Low-effort costumes and aggressively honest gifts.",
    tone: "holiday",
    artLabel: "Festive enough.",
    artNote: "The Holiday Collection"
  }
];

const colorLabels: Record<string, string> = {
  black: "#111111",
  white: "#f4efe7",
  heather: "#a6a19a",
  navy: "#14243b",
  "dark heather": "#454545",
  maroon: "#642838",
  natural: "#fff1d5",
  "sport grey": "#cacaca",
  ash: "#ededeb"
};

const defaultColorPriority = ["black", "navy", "maroon", "dark heather", "natural", "sport grey", "ash", "white"];

function stableProductIndex(value: string, length: number) {
  if (length <= 0) return 0;
  return Array.from(value).reduce((sum, character) => sum + character.charCodeAt(0), 0) % length;
}

function defaultProductColor(product: Product) {
  const available = defaultColorPriority.filter((color) => product.colors.includes(color));
  const nonWhite = available.filter((color) => color !== "white");
  const choices = nonWhite.length ? nonWhite : available.length ? available : product.colors;
  return choices[stableProductIndex(product.id, choices.length)] || product.colors[0];
}

export default function App() {
  const [catalog, setCatalog] = useState<Product[]>(products);
  const [catalogCollections, setCatalogCollections] = useState<ProductCollection[]>([]);
  const [catalogError, setCatalogError] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCollection, setActiveCollection] = useState("All");
  const [maxPrice, setMaxPrice] = useState("All");
  const [activeSize, setActiveSize] = useState("All");
  const [cookieChoice, setCookieChoice] = useState(() => localStorage.getItem("ihs_cookie_choice") || "");
  const route = normalizeRoute(window.location.pathname);

  useEffect(() => {
    fetch("/api/products")
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: CatalogPayload | null) => {
        if (!payload?.products?.length) {
          setCatalogError(payload?.error || "No Shopify products returned yet.");
          return;
        }
        setCatalog(payload.products);
        setCatalogCollections(payload.collections || []);
        setCatalogError("");
      })
      .catch(() => {
        setCatalog(products);
        setCatalogError("Shopify catalog is temporarily unavailable.");
      });
  }, []);

  const currentLanding = useMemo(() => {
    const knownLanding = landingPages.find((page) => page.slug === route);
    if (knownLanding) return knownLanding;
    const liveCollection = catalogCollections.find((collection) => collection.handle === route);
    if (!liveCollection) return undefined;
    return {
      slug: liveCollection.handle,
      label: liveCollection.title,
      collection: liveCollection.title,
      collectionHandles: [liveCollection.handle],
      tags: [liveCollection.title.toLowerCase()],
      kicker: "Collection drop",
      headline: `${liveCollection.title} built like its own mini-store.`,
      body: `Every ${liveCollection.title.toLowerCase()} design in one place, ready to browse by size and price.`,
      tone: "default",
      artLabel: liveCollection.title,
      artNote: "Shop the collection"
    };
  }, [catalogCollections, route]);
  const collections = useMemo(() => {
    const liveCollections = catalogCollections.length
      ? catalogCollections.map((collection) => collection.title)
      : Array.from(new Set(catalog.map((product) => product.collection)));
    return ["All", ...liveCollections];
  }, [catalog, catalogCollections]);
  const visibleProducts = useMemo(() => {
    return catalog.filter((product) => {
      const collectionTarget = currentLanding?.collection || activeCollection;
      const landingCollectionMatch = currentLanding
        ? currentLanding.collectionHandles.includes(product.collectionHandle) || product.collection === currentLanding.collection
        : false;
      const collectionMatch = currentLanding
        ? landingCollectionMatch
        : collectionTarget === "All" || product.collection === collectionTarget || product.collections.some((collection) => collection.title === collectionTarget);
      const searchTarget = `${product.title} ${product.collection} ${product.productType} ${product.tags.join(" ")} ${product.buyer} ${product.trigger} ${product.phrase}`.toLowerCase();
      const queryMatch = searchTarget.includes(query.toLowerCase());
      const priceMatch = maxPrice === "All" || product.price <= Number(maxPrice);
      const sizeMatch = activeSize === "All" || product.sizes.includes(activeSize);
      return collectionMatch && queryMatch && priceMatch && sizeMatch;
    });
  }, [activeCollection, activeSize, catalog, currentLanding, maxPrice, query]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  function chooseCookie(choice: string) {
    localStorage.setItem("ihs_cookie_choice", choice);
    setCookieChoice(choice);
  }

  function addToCart(product: Product, size: string, color: string) {
    setCart((items) => {
      const match = items.find((item) => item.product.id === product.id && item.size === size && item.color === color);
      if (match) return items.map((item) => (item === match ? { ...item, quantity: item.quantity + 1 } : item));
      const variant = product.variants.find((item) => item.size === size && item.color === color);
      const image = variant?.image || product.imagesByColor[color] || product.image;
      return [...items, { product, image, size, color, quantity: 1, variantId: variant?.id, unitPrice: variant?.price || product.price }];
    });
    setCartOpen(true);
  }

  function updateQuantity(target: CartItem, direction: 1 | -1) {
    setCart((items) =>
      items
        .map((item) => (item === target ? { ...item, quantity: item.quantity + direction } : item))
        .filter((item) => item.quantity > 0)
    );
  }

  async function checkout() {
    setCheckoutError("");
    const lines = cart.filter((item) => item.variantId).map((item) => ({ variantId: String(item.variantId), quantity: item.quantity }));

    if (hasShopifyConfig && lines.length === cart.length) {
      try {
        window.location.href = await createShopifyCheckout(lines);
        return;
      } catch (error) {
        setCheckoutError(error instanceof Error ? error.message : "Shopify checkout failed.");
        return;
      }
    }

    if (lines.length === cart.length && lines.length > 0) {
      window.location.href = createCartPermalink(lines);
      return;
    }

    setCheckoutError("One or more shirts in this cart are preview-only and are not published in Shopify yet.");
  }

  return (
    <>
      <Header cartCount={cartCount} menuOpen={menuOpen} setMenuOpen={setMenuOpen} openCart={() => setCartOpen(true)} />

      <main id="top">
        {currentLanding ? (
          <CollectionStore
            landing={currentLanding}
            products={visibleProducts}
            query={query}
            setQuery={setQuery}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            activeSize={activeSize}
            setActiveSize={setActiveSize}
            onAdd={addToCart}
            onView={setSelectedProduct}
            catalogError={catalogError}
          />
        ) : (
          <HomePage
            catalog={catalog}
            collections={collections}
            activeCollection={activeCollection}
            setActiveCollection={setActiveCollection}
            query={query}
            setQuery={setQuery}
            products={visibleProducts}
            onAdd={addToCart}
            onView={setSelectedProduct}
            catalogError={catalogError}
          />
        )}
      </main>

      <SiteFooter />
      {!cookieChoice ? <CookieBanner onChoose={chooseCookie} /> : null}
      <CartDrawer cart={cart} subtotal={subtotal} open={cartOpen} checkoutError={checkoutError} onClose={() => setCartOpen(false)} onCheckout={checkout} onUpdateQuantity={updateQuantity} />
      <ProductDetail product={selectedProduct} onAdd={addToCart} onClose={() => setSelectedProduct(null)} />
    </>
  );
}

function normalizeRoute(pathname: string) {
  const route = pathname.replace(/^\/+|\/+$/g, "");
  return route === "wizardry" ? "witchy" : route;
}

function storefrontCollectionName(name: string) {
  return /wizard/i.test(name) ? "Witchy Collection" : name;
}

function Header({
  cartCount,
  menuOpen,
  setMenuOpen,
  openCart
}: {
  cartCount: number;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  openCart: () => void;
}) {
  return (
    <header className="site-header retail-header">
      <a className="brand" href="/" aria-label="I Hate Shirts home">
        <span className="brand-mark">IHS</span>
        <span>I Hate Shirts</span>
      </a>
      <nav className={menuOpen ? "nav nav-open" : "nav"} aria-label="Primary navigation">
        <a href="/#collections">Featured</a>
        <a href="/#collections">Collections</a>
        <a href="/#catalog">Shop all</a>
        <a href="/horny">After dark</a>
      </nav>
      <div className="header-actions">
        <button className="icon-button" type="button" aria-label="Open menu" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <button className="cart-button" type="button" onClick={openCart}>
          <ShoppingBag size={18} />
          <span>Cart</span>
          <strong>{cartCount}</strong>
        </button>
      </div>
    </header>
  );
}

function HomePage({
  catalog,
  collections,
  activeCollection,
  setActiveCollection,
  query,
  setQuery,
  products,
  onAdd,
  onView,
  catalogError
}: {
  catalog: Product[];
  collections: string[];
  activeCollection: string;
  setActiveCollection: (collection: string) => void;
  query: string;
  setQuery: (query: string) => void;
  products: Product[];
  onAdd: (product: Product, size: string, color: string) => void;
  onView: (product: Product) => void;
  catalogError: string;
}) {
  const featuredCollections = landingPages.slice(0, 2).map((landing) => ({
    landing,
    products: catalog.filter((product) =>
      landing.collectionHandles.includes(product.collectionHandle) || product.collection === landing.collection
    )
  }));

  return (
    <>
      <HeroSlider />
      <div id="collections">
        {featuredCollections.map(({ landing, products: collectionProducts }, index) => (
          <section className={`featured-collection ${landing.tone}`} key={landing.slug}>
            <a className="collection-banner" href={`/${landing.slug}`} aria-label={`Shop the ${landing.label} Collection`}>
              <img src={`/banners/${landing.slug}-placeholder.svg`} alt="" />
            </a>
            <div className="featured-collection-heading">
              <span>Collection {String(index + 1).padStart(2, "0")}</span>
              <h2>{landing.label} Collection</h2>
              <a href={`/${landing.slug}`}>View collection <ChevronRight size={18} /></a>
            </div>
            {catalogError ? <p className="catalog-status">{catalogError}</p> : null}
            <ProductGrid products={collectionProducts} onAdd={onAdd} onView={onView} />
          </section>
        ))}
      </div>
      <section className="retail-section" id="catalog">
        <SectionTitle kicker="Every bad idea, one place" title="The whole catalog." body="Search every collection, phrase, size, and questionable decision." />
        <CatalogTools collections={collections} activeCollection={activeCollection} setActiveCollection={setActiveCollection} query={query} setQuery={setQuery} />
        <ProductGrid products={products} onAdd={onAdd} onView={onView} />
      </section>
    </>
  );
}

function HeroSlider() {
  const slides = landingPages.slice(0, 2);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setActive((current) => (current + 1) % slides.length), 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="retail-hero" aria-roledescription="carousel" aria-label="Featured collections">
      <div className="hero-track" style={{ transform: `translate3d(-${active * 100}%, 0, 0)` }}>
        {slides.map((page, index) => (
          <a className={`hero-slide ${page.tone}`} href={`/${page.slug}`} aria-hidden={active !== index} tabIndex={active === index ? 0 : -1} key={page.slug}>
            <img src={`/banners/${page.slug}-placeholder.svg`} alt={`Shop the ${page.label} Collection`} />
          </a>
        ))}
      </div>
      <div className="slide-controls" aria-label="Hero slides">
        <button className="slide-arrow" type="button" aria-label="Previous collection" onClick={() => setActive((active - 1 + slides.length) % slides.length)}>
          <ChevronLeft size={17} />
        </button>
        <button className="slide-arrow" type="button" aria-label="Next collection" onClick={() => setActive((active + 1) % slides.length)}>
          <ChevronRight size={17} />
        </button>
      </div>
    </section>
  );
}

function CollectionStore({
  landing,
  products,
  query,
  setQuery,
  maxPrice,
  setMaxPrice,
  activeSize,
  setActiveSize,
  onAdd,
  onView,
  catalogError
}: {
  landing: LandingPage;
  products: Product[];
  query: string;
  setQuery: (query: string) => void;
  maxPrice: string;
  setMaxPrice: (price: string) => void;
  activeSize: string;
  setActiveSize: (size: string) => void;
  onAdd: (product: Product, size: string, color: string) => void;
  onView: (product: Product) => void;
  catalogError: string;
}) {
  return (
    <>
      <section className={`collection-hero ${landing.tone}`}>
        <div>
          <span className="soft-eyebrow">{landing.kicker}</span>
          <h1>{landing.label}</h1>
          <p>{landing.body}</p>
        </div>
        <div className="collection-art" aria-hidden="true">
          <span>{landing.artNote}</span>
          <strong>{landing.artLabel}</strong>
        </div>
      </section>
      <section className="retail-section">
        <div className="store-toolbar">
          <div className="search-wrap">
            <Search size={18} />
            <input type="search" placeholder={`Search ${landing.label.toLowerCase()}`} value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
          <label className="select-wrap">
            <span>Price</span>
            <select value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)}>
              <option>All</option>
              <option value="24">$24 and under</option>
              <option value="26">$26 and under</option>
              <option value="30">$30 and under</option>
            </select>
            <ChevronDown size={16} />
          </label>
          <label className="select-wrap">
            <span>Size</span>
            <select value={activeSize} onChange={(event) => setActiveSize(event.target.value)}>
              <option>All</option>
              {["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"].map((size) => (
                <option key={size}>{size}</option>
              ))}
            </select>
            <ChevronDown size={16} />
          </label>
          <button className="filter-label" type="button">
            <Filter size={18} />
            {products.length} shown
          </button>
        </div>
        {catalogError ? <p className="catalog-status">{catalogError}</p> : null}
        <ProductGrid products={products} onAdd={onAdd} onView={onView} />
      </section>
    </>
  );
}

function CatalogTools({
  collections,
  activeCollection,
  setActiveCollection,
  query,
  setQuery
}: {
  collections: string[];
  activeCollection: string;
  setActiveCollection: (collection: string) => void;
  query: string;
  setQuery: (query: string) => void;
}) {
  return (
    <div className="catalog-tools">
      <div className="search-wrap">
        <Search size={18} />
        <input type="search" placeholder="Search shirts, niches, phrases" value={query} onChange={(event) => setQuery(event.target.value)} />
      </div>
      <div className="collection-tabs" role="tablist" aria-label="Product collections">
        {collections.map((collection) => (
          <button className={activeCollection === collection ? "active" : ""} type="button" key={collection} onClick={() => setActiveCollection(collection)}>
            {storefrontCollectionName(collection)}
          </button>
        ))}
      </div>
    </div>
  );
}

function SectionTitle({ kicker, title, body }: { kicker: string; title: string; body: string }) {
  return (
    <div className="retail-heading">
      <span className="soft-eyebrow">{kicker}</span>
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}

function ProductGrid({ products, onAdd, onView }: { products: Product[]; onAdd: (product: Product, size: string, color: string) => void; onView: (product: Product) => void }) {
  return (
    <section className="product-grid retail-grid" aria-label="Product grid">
      {products.map((product) => (
        <ProductCard product={product} key={product.id} onAdd={onAdd} onView={onView} />
      ))}
      {products.length === 0 ? <p className="catalog-status">No live Shopify products match this view yet.</p> : null}
    </section>
  );
}

function ProductCard({ product, onAdd, onView }: { product: Product; onAdd: (product: Product, size: string, color: string) => void; onView: (product: Product) => void }) {
  const size = product.sizes.includes("L") ? "L" : product.sizes[0];
  const [color, setColor] = useState(() => defaultProductColor(product));
  const selectedVariant = product.variants.find((item) => item.size === size && item.color === color && item.available !== false);
  const displayPrice = selectedVariant?.price || product.price;
  const displayImage = selectedVariant?.image || product.imagesByColor[color] || product.image;
  const canBuy = Boolean(selectedVariant);
  const cardTone = stableProductIndex(product.id, 5);

  return (
    <article className={`product-card retail-product card-tone-${cardTone}`}>
      <button className="product-image" type="button" onClick={() => onView(product)} aria-label={`View ${product.title}`}>
        <img src={displayImage} alt={`${product.title} in ${color}`} loading="lazy" />
        <span>Quick view</span>
      </button>
      <div className="product-copy">
        <div className="product-meta">
          <span>{storefrontCollectionName(product.collection)}</span>
          <span>{product.colors.length} colors</span>
        </div>
        <h3>{product.phrase}</h3>
        <div className="card-swatches" aria-label={`${product.colors.length} available colors`}>
          {product.colors.map((option) => (
            <button className={option === color ? "active" : ""} style={{ background: colorLabels[option] || option }} type="button" title={option} aria-label={`Show ${product.phrase} in ${option}`} aria-pressed={option === color} key={option} onClick={() => setColor(option)} />
          ))}
        </div>
      </div>
      <div className="product-actions">
        <div><small>From</small><strong>${displayPrice.toFixed(2)}</strong></div>
        <button className="view-button" type="button" onClick={() => onView(product)} aria-label={`Choose options for ${product.title}`}>
          <Eye size={17} />
          View
        </button>
        <button type="button" disabled={!canBuy} onClick={() => onAdd(product, size, color)}>
          <Plus size={17} />
          {canBuy ? "Quick add" : "Preview"}
        </button>
      </div>
    </article>
  );
}

function ProductDetail({ product, onAdd, onClose }: { product: Product | null; onAdd: (product: Product, size: string, color: string) => void; onClose: () => void }) {
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");

  useEffect(() => {
    if (!product) return;
    setSize(product.sizes[2] || product.sizes[0]);
    setColor(defaultProductColor(product));
  }, [product]);

  if (!product) return null;
  const selectedVariant = product.variants.find((item) => item.size === size && item.color === color && item.available !== false);
  const displayPrice = selectedVariant?.price || product.price;
  const displayImage = selectedVariant?.image || product.imagesByColor[color] || product.image;
  const canBuy = Boolean(selectedVariant);

  return (
    <aside className="product-detail" aria-label={`${product.title} details`}>
      <button className="detail-scrim" type="button" aria-label="Close product details" onClick={onClose} />
      <div className="detail-panel">
        <button className="icon-button" type="button" aria-label="Close product details" onClick={onClose}>
          <X size={20} />
        </button>
        <div className={`detail-media card-tone-${stableProductIndex(product.id, 5)}`}>
          <img src={displayImage} alt={`${product.title} in ${color}`} key={displayImage} />
        </div>
        <div className="detail-copy">
          <span className="soft-eyebrow">{storefrontCollectionName(product.collection)}</span>
          <h2>{product.phrase}</h2>
          <p>{product.description || "A made-to-order unisex tee for people who prefer their shirts with a little more personality."}</p>
          <div className="detail-facts">
            <span>Unisex fit</span>
            <span>Made to order</span>
            <span>{canBuy ? "Secure Shopify checkout" : "Preview only"}</span>
          </div>
          <div className="selector-row" aria-label={`Color selection for ${product.title}`}>
            {product.colors.map((option) => (
              <button className={option === color ? "swatch active" : "swatch"} type="button" style={{ background: colorLabels[option] || option }} aria-label={option} title={option} key={option} onClick={() => setColor(option)} />
            ))}
          </div>
          <label className="select-wrap">
            <span>Size</span>
            <select value={size} onChange={(event) => setSize(event.target.value)}>
              {product.sizes.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            <ChevronDown size={16} />
          </label>
          <div className="product-actions">
            <strong>${displayPrice.toFixed(2)}</strong>
            <button type="button" disabled={!canBuy} onClick={() => { onAdd(product, size, color); onClose(); }}>
              <Plus size={17} />
              {canBuy ? "Add to cart" : "Preview only"}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function CartDrawer({ cart, subtotal, open, checkoutError, onClose, onCheckout, onUpdateQuantity }: { cart: CartItem[]; subtotal: number; open: boolean; checkoutError: string; onClose: () => void; onCheckout: () => void; onUpdateQuantity: (target: CartItem, direction: 1 | -1) => void }) {
  return (
    <>
      {open ? <button className="cart-scrim" type="button" aria-label="Close shopping cart" onClick={onClose} /> : null}
      <aside className={open ? "cart-drawer open" : "cart-drawer"} aria-label="Shopping cart" aria-hidden={!open}>
      <div className="cart-header">
        <div>
          <span className="soft-eyebrow">Checkout lane</span>
          <h2>Your cart</h2>
        </div>
        <button className="icon-button" type="button" aria-label="Close cart" onClick={onClose}>
          <X size={20} />
        </button>
      </div>
      <div className="cart-items">
        {cart.length === 0 ? (
          <div className="empty-cart">
            <SlidersHorizontal size={24} />
            <p>Your questionable decisions will appear here.</p>
          </div>
        ) : (
          cart.map((item) => (
            <div className="cart-item" key={`${item.product.id}-${item.size}-${item.color}`}>
              <img src={item.image} alt={`${item.product.phrase} in ${item.color}`} />
              <div>
                <strong>{item.product.phrase}</strong>
                <span>{item.size} / {item.color}</span>
                <div className="quantity">
                  <button type="button" aria-label="Decrease quantity" onClick={() => onUpdateQuantity(item, -1)}><Minus size={14} /></button>
                  <span>{item.quantity}</span>
                  <button type="button" aria-label="Increase quantity" onClick={() => onUpdateQuantity(item, 1)}><Plus size={14} /></button>
                </div>
              </div>
              <span>${(item.unitPrice * item.quantity).toFixed(2)}</span>
            </div>
          ))
        )}
      </div>
      <div className="cart-footer">
        <div className="subtotal"><span>Subtotal</span><strong>${subtotal.toFixed(2)}</strong></div>
        {checkoutError ? <p className="checkout-error">{checkoutError}</p> : null}
        <button className="checkout-button" type="button" disabled={cart.length === 0} onClick={onCheckout}>
          <Check size={18} />
          Checkout securely
        </button>
      </div>
      </aside>
    </>
  );
}

function CookieBanner({ onChoose }: { onChoose: (choice: string) => void }) {
  return (
    <aside className="cookie-banner" aria-label="Cookie preferences">
      <div>
        <strong>Cookie preferences</strong>
        <p>Essential cookies keep checkout working. Optional analytics help us learn which bad ideas deserve another print run.</p>
      </div>
      <div className="cookie-actions">
        <button type="button" onClick={() => onChoose("essential")}>Essential only</button>
        <button type="button" onClick={() => onChoose("all")}>Accept all</button>
      </div>
    </aside>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-brand">
        <strong>I Hate Shirts</strong>
        <p>Wear the thought you were supposed to keep to yourself.</p>
      </div>
      <div className="footer-column">
        <strong>Shop</strong>
        <nav aria-label="Shop collections">
          <a href="/witchy">Witchy Collection</a>
          <a href="/horny">Horny Collection</a>
          <a href="/nurses">Nurse Shirts</a>
          <a href="/work-shirts">Work Shirts</a>
          <a href="/holiday">Holiday Shirts</a>
        </nav>
      </div>
      <div className="footer-column">
        <strong>Help</strong>
        <nav aria-label="Customer help">
          <a href="/#catalog">Search catalog</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </nav>
      </div>
      <div className="footer-column footer-note">
        <strong>Made to make noise</strong>
        <p>Independent designs, printed to order, secured by Shopify checkout.</p>
      </div>
      <small>© {new Date().getFullYear()} I Hate Shirts. All rights reserved.</small>
    </footer>
  );
}
